import {
  type Dispatch,
  type FC,
  memo,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { type RectReadOnly } from 'react-use-measure';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import Decimal from 'decimal.js';

import {
  useClaimMaterialByIdMutation,
  usePutToFactoryMutation,
} from '@/api/queries/factory';
import { AppRoute } from '@/common/app/routes';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { gameItemOrder } from '@/common/constants';
import { useCheckStorageOverflow } from '@/common/hooks/check-storage-overflow';
import { useMaxProduction } from '@/common/hooks/max-production';
import { calcToProduceValue } from '@/common/lib/calcToProduceValue';
import {
  firebaseLogCollectResource,
  firebaseLogResourceProcessing,
} from '@/common/lib/firebaseLog';
import { playSound } from '@/common/lib/soundEffect';
import { type UserFactory } from '@/common/types/factoryType';
import { IntroContext } from '@/components/Intro/Intro';
import { ProductionButton } from '@/components/Plant/ProductionButtons/ProductionButtons';
import { ProductionScale } from '@/components/Plant/ProductionScale/ProductionScale';
import { ToUpgrade } from '@/components/ToUpgrade/ToUpgrade';
import { ProducePercentage } from '@/components/Workshop/WorkshopProduction/ProducePercentage/ProducePercentage';
import { changeStorage } from '@/redux/reducers/userReducer';
import {
  selectFactoryByIdOrName,
  selectTotalFactoryBoostById,
} from '@/redux/selectors/factoryReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { BoostFactoryButton } from '../BoostFactoryButton/BoostFactoryButton';

import stockPng from '/img/tabbar/office.png';
import stockWebp from '/img/tabbar/office.webp';

interface FactoryProductionProps {
  plantId: string;
  openBoostFactory: () => void;
  isComplete: boolean;
  incommingStorage: number;
  toAdd?: number;
  setToAdd: Dispatch<SetStateAction<number | undefined>>;
  producted: number;
  localStock: number;
  limit?: number;
  setPullCoords: Dispatch<SetStateAction<RectReadOnly | null>>;
  setPullAnimating: Dispatch<SetStateAction<boolean>>;
}

const FactoryProduction: FC<FactoryProductionProps> = memo(
  ({
    plantId,
    openBoostFactory,
    isComplete,
    limit,
    toAdd,
    producted,
    incommingStorage,
    setToAdd,
    localStock,
    setPullCoords,
    setPullAnimating,
  }) => {
    const ref = useHighlightComponentRect(14);
    const factory = useAppSelector((state) =>
      selectFactoryByIdOrName(state, plantId),
    ) as UserFactory;
    const storage = useAppSelector((state) => state.user.user?.storage);
    const { t } = useTranslation();
    const [claimMaterial, claimMaterialApiState] =
      useClaimMaterialByIdMutation();
    const [putToFactory, putToFactoryApiState] = usePutToFactoryMutation({
      fixedCacheKey: 'putToFactoryProduction',
    });
    const totalBoost = useAppSelector((state) =>
      selectTotalFactoryBoostById(state, plantId),
    );
    const { activeStep } = useContext(IntroContext);

    const checkStockOverflow = useCheckStorageOverflow();
    const [percentage, setPercentage] = useState(100);
    const dispatch = useAppDispatch();
    const max = useMaxProduction({ [factory.source]: factory.rate });
    const setToProduction = useCallback(
      (percentage: number): void => {
        if (limit) {
          const toAdd = calcToProduceValue({
            percentage,
            limit,
            incommingStorage,
            localStock,
            max,
          });
          setToAdd(toAdd);
        }
      },
      [limit, incommingStorage, localStock, max],
    );

    useEffect(() => {
      if (factory.incommingStorage !== undefined) {
        const toAdd = calcToProduceValue({
          percentage: 100,
          limit: factory.storageCapacity,
          incommingStorage: factory.incommingStorage,
          localStock: factory.localStock || 0,
          max,
        });
        setToAdd(toAdd);
      }
    }, [
      plantId,
      factory.storageCapacity,
      factory.incommingStorage,
      factory.localStock,
      max,
    ]);

    useEffect(() => {
      setPercentage(100);
    }, [plantId, incommingStorage]);

    const addToProduction = (): void => {
      if (factory) {
        setPullAnimating(true);
        playSound('processing/full');
        const amount = new Decimal(toAdd ?? 0).mul(factory.rate).toNumber();
        putToFactory({ amount: toAdd ?? 0, plantId: factory.id })
          .unwrap()
          .then(() => {
            dispatch(changeStorage({ [factory.source]: amount }));
            setToAdd(0);
            firebaseLogResourceProcessing(factory, amount);
          });
      }
    };

    const onClaim = (): void => {
      playSound('buttons/claim');
      const prevStorage = Object.assign({}, storage);
      claimMaterial({ id: plantId })
        .unwrap()
        .then(({ plant, storage }) => {
          checkStockOverflow(storage, gameItemOrder.material);
          ToastNotification(t('Materials sent to Stock'), {
            img: stockPng,
            webp: stockWebp,
          });
          firebaseLogCollectResource(plant, storage, prevStorage);
        });
    };

    return (
      factory &&
      typeof toAdd === 'number' && (
        <div className="w-full flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <ToUpgrade
              link={AppRoute.FACTORY + `/${plantId}/upgrade`}
              isComplete={isComplete}
            />
            <div className="relative flex-1 h-12">
              <ProductionScale
                activeIsTarget={true}
                toAdd={toAdd}
                limit={limit}
                localStock={localStock}
                incommingStorage={incommingStorage}
                productionTimeLeft={factory?.productionTimeLeft}
                setPullCoords={setPullCoords}
              />
            </div>
            <div id="intro-step-14" ref={ref}>
              <BoostFactoryButton
                totalBoost={totalBoost || 0}
                onClick={openBoostFactory}
                plantId={plantId}
              />
            </div>
          </div>
          <ProducePercentage
            onClick={setToProduction}
            percentage={percentage}
            setPercentage={setPercentage}
          />
          <ProductionButton
            claimButtonProps={{
              onClick: onClaim,
              disabled:
                (!(localStock + producted) ||
                  claimMaterialApiState.isLoading) &&
                activeStep !== 17,
              inactive: !(localStock + producted) && activeStep !== 17,
              isLoading: claimMaterialApiState.isLoading,
            }}
            produceButtonProps={{
              onClick: addToProduction,
              disabled: !toAdd || putToFactoryApiState.isLoading,
              inactive: !toAdd,
              isLoading: putToFactoryApiState.isLoading,
            }}
            localStock={localStock}
            toAdd={toAdd}
            itemName={factory?.name}
            producted={activeStep === 17 ? Math.max(producted, 12) : producted}
            recipe={{ [factory.source]: factory.rate }}
          />
        </div>
      )
    );
  },
);

export { FactoryProduction };
